import { useState } from 'react';
import { ConfirmDialog } from '@/components/Modal';

export type SelectedCell = { nodeId: string; nodeName: string; targetMonth: string };

type Props = {
  selected: SelectedCell[];
  canPublish: boolean;
  canUnpublish: boolean;
  onPublish: (cells: SelectedCell[]) => Promise<void>;
  onUnpublish: (cells: SelectedCell[]) => Promise<void>;
  onClearSelection: () => void;
};

/**
 * v0.3 (S-7): 一括公開/非公開ツールバー。
 * Design C3対応: 一括操作は必ずConfirmDialog経由、対象件数+対象店舗/月リストを表示、
 * unpublishはdestructive=trueでCancelを初期フォーカス。
 */
export const BulkActionsToolbar = ({ selected, canPublish, canUnpublish, onPublish, onUnpublish, onClearSelection }: Props) => {
  const [confirming, setConfirming] = useState<'publish' | 'unpublish' | null>(null);
  const [loading, setLoading] = useState(false);

  if (selected.length === 0) return null;

  const listLabel = selected
    .map((c) => `${c.nodeName} ${c.targetMonth}`)
    .slice(0, 10)
    .join('、') + (selected.length > 10 ? ` 他${selected.length - 10}件` : '');

  const runAction = async () => {
    setLoading(true);
    try {
      if (confirming === 'publish') await onPublish(selected);
      else if (confirming === 'unpublish') await onUnpublish(selected);
      onClearSelection();
    } finally {
      setLoading(false);
      setConfirming(null);
    }
  };

  return (
    <>
      <div className="sticky bottom-0 z-20 flex flex-wrap items-center gap-3 rounded-lg border border-surface-border bg-white px-4 py-3 shadow-overlay">
        <span className="text-[13px] font-bold text-text-body">{selected.length}件選択中</span>
        <div className="flex gap-2 ml-auto">
          <button type="button" onClick={onClearSelection}
            className="h-10 px-4 rounded-md bg-white text-text-body text-[13px] font-extrabold btn-press-secondary hover:bg-surface-sunken focus-ring min-h-[44px] sm:min-h-[40px]">
            選択解除
          </button>
          {canPublish && (
            <button type="button" onClick={() => setConfirming('publish')}
              className="h-10 px-4 rounded-md bg-brand-red text-white text-[13px] font-extrabold btn-press-primary hover:bg-brand-red-hover focus-ring min-h-[44px] sm:min-h-[40px]">
              一括公開
            </button>
          )}
          {canUnpublish && (
            <button type="button" onClick={() => setConfirming('unpublish')}
              className="h-10 px-4 rounded-md bg-white text-danger-text border border-danger-border text-[13px] font-extrabold btn-press-secondary hover:bg-danger-bg focus-ring min-h-[44px] sm:min-h-[40px]">
              一括非公開
            </button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirming !== null}
        title={confirming === 'publish' ? '一括公開の確認' : '一括非公開の確認'}
        destructive={confirming === 'unpublish'}
        confirmLabel={confirming === 'publish' ? '公開する' : '非公開にする'}
        loading={loading}
        onClose={() => { if (!loading) setConfirming(null); }}
        onConfirm={runAction}
        message={
          <div>
            <p className="mb-2">
              以下 {selected.length} 件を{confirming === 'publish' ? '公開' : '非公開に'}します。
              {confirming === 'publish' && '他の閲覧者全員に数値・AIコメントが公開されます。'}
            </p>
            <p className="text-[12px] text-text-sub break-words">{listLabel}</p>
          </div>
        }
      />
    </>
  );
};
